import { Link } from 'react-router-dom';
import ShieldIcon from '../components/ShieldIcon';
import { brand } from '../config/brand';

export default function TermsPage() {
  return (
    <div className="legal-page">
      <div className="legal-topbar">
        <Link to="/" className="legal-topbar-brand">
          <ShieldIcon size={22} />
          {brand.name}
        </Link>
      </div>

      <main className="legal-content">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: March 2026</p>

        <p>
          These Terms of Service ("Terms") govern your use of the {brand.name} biometric liveness
          verification service operated at {brand.domain}. By accessing or using the service, you
          agree to be bound by these Terms. If you do not agree to these Terms, do not use the
          service.
        </p>

        <h2>1. Acceptance of Terms</h2>
        <p>
          By creating an account, sending a verification link, or completing a liveness check, you
          acknowledge that you have read, understood, and agree to be bound by these Terms and our{' '}
          <Link to="/privacy">Privacy Policy</Link>. These Terms apply to all users of the service,
          including recruiters (account holders) and candidates (verification subjects).
        </p>

        <h2>2. Service Description</h2>
        <p>
          {brand.name} provides a biometric liveness verification service designed for recruitment
          and hiring workflows. The service allows recruiters to:
        </p>
        <ul>
          <li>Create identity verification sessions for candidates</li>
          <li>Send candidates a secure link to complete a face liveness check</li>
          <li>Receive verification results indicating whether the candidate is a live person</li>
        </ul>
        <p>
          The service uses AWS Rekognition Face Liveness technology to analyze facial data in
          real-time. No biometric data is stored by {brand.name} — facial images are processed
          transiently by AWS and discarded immediately after analysis.
        </p>

        <h2>3. User Accounts</h2>
        <p>
          Recruiters must create an account to use {brand.name}. You are responsible for maintaining
          the confidentiality of your account credentials and for all activity that occurs under
          your account. You agree to notify us immediately of any unauthorized use of your account.
        </p>

        <h2>4. User Responsibilities</h2>
        <p>By using {brand.name}, you agree to:</p>
        <ul>
          <li>Provide accurate and truthful information when creating accounts and verification sessions</li>
          <li>Use the service only for lawful purposes related to identity verification in recruitment, hiring, or similar legitimate professional contexts</li>
          <li>Obtain appropriate consent from candidates before sending them a verification link</li>
          <li>Comply with all applicable laws and regulations, including data protection and biometric privacy laws in your jurisdiction</li>
          <li>Not share verification links with anyone other than the intended candidate</li>
          <li>Not use the service to discriminate against any individual or group</li>
        </ul>

        <h2>5. Candidate Consent</h2>
        <p>
          Candidates are informed that they are undergoing a biometric liveness check before the
          scan begins. By proceeding with the verification, candidates consent to having their
          facial data processed by AWS Rekognition for the purpose of liveness detection. Candidates
          may decline the check at any time by closing their browser window.
        </p>

        <h2>6. Prohibited Uses</h2>
        <p>You may not use {brand.name} to:</p>
        <ul>
          <li>Conduct surveillance or monitor individuals without their knowledge and consent</li>
          <li>Build facial recognition databases or biometric profiles</li>
          <li>Verify the identity of minors (individuals under the age of 18)</li>
          <li>Attempt to reverse-engineer, decompile, or extract the underlying technology</li>
          <li>Interfere with or disrupt the service, servers, or networks connected to the service</li>
          <li>Use automated tools, bots, or scripts to access or interact with the service</li>
          <li>Circumvent any security measures or access controls</li>
          <li>Use the service for any purpose that violates applicable law</li>
        </ul>

        <h2>7. Service Availability and Accuracy</h2>
        <p>
          {brand.name} strives to provide reliable and accurate liveness verification. However, we
          do not guarantee that the service will be uninterrupted, error-free, or that liveness
          results will be 100% accurate in every case. Verification results should be used as one
          component of your identity verification process, not as the sole basis for employment or
          other consequential decisions.
        </p>

        <h2>8. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, {brand.name} and its operators shall
          not be liable for any indirect, incidental, special, consequential, or punitive damages,
          including but not limited to loss of profits, data, or business opportunities, arising out
          of or related to your use of or inability to use the service.
        </p>
        <p>
          {brand.name} is provided on an "as is" and "as available" basis without warranties of any
          kind, either express or implied, including but not limited to implied warranties of
          merchantability, fitness for a particular purpose, or non-infringement.
        </p>

        <h2>9. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless {brand.name} and its operators from and against
          any claims, liabilities, damages, losses, and expenses arising from your use of the
          service, your violation of these Terms, or your violation of any rights of another party.
        </p>

        <h2>10. Termination</h2>
        <p>
          We reserve the right to suspend or terminate your access to {brand.name} at any time, with
          or without cause, and with or without notice. Upon termination, your right to use the
          service ceases immediately. Session data is retained according to our standard 7-day
          retention policy and will be automatically deleted thereafter. You may also delete your
          account at any time by contacting us.
        </p>

        <h2>11. Changes to These Terms</h2>
        <p>
          We may revise these Terms at any time by updating this page. The "Last updated" date at
          the top indicates when the Terms were last modified. Your continued use of {brand.name}{' '}
          after any changes constitutes your acceptance of the new Terms. We encourage you to review
          these Terms periodically.
        </p>

        <h2>12. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with applicable law, without
          regard to conflict of law principles.
        </p>

        <h2>13. Contact Us</h2>
        <p>
          If you have any questions about these Terms of Service, please contact us at{' '}
          <a href="mailto:legal@aliv.me">legal@aliv.me</a>.
        </p>

        <div className="legal-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/login">Sign In</Link>
        </div>
      </main>
    </div>
  );
}
